/**
 * Hungarian algorithm for minimum-cost assignment.
 * Solves: assign N tracks to M detections to minimize total cost.
 * Cost matrix: cost[i][j] = distance between track i and detection j.
 */
export function hungarianAssignment(costMatrix: number[][]): number[] {
  const n = costMatrix.length;
  const m = costMatrix[0]?.length ?? 0;
  if (n === 0 || m === 0) return [];

  const INF = 1e9;
  const dim = Math.max(n, m) + 1;
  const u = new Array(dim).fill(0);
  const v = new Array(dim).fill(0);
  const p = new Array(dim).fill(0);
  const way = new Array(dim).fill(0);

  const cost = (i: number, j: number): number =>
    i < n && j < m ? costMatrix[i][j] : 0;

  for (let i = 1; i <= n; i++) {
    p[0] = i;
    let j0 = 0;
    const minv = new Array(dim).fill(INF);
    const used = new Array(dim).fill(false);

    do {
      used[j0] = true;
      const i0 = p[j0];
      let delta = INF;
      let j1 = 0;

      for (let j = 1; j <= m; j++) {
        if (used[j]) continue;
        const cur = cost(i0 - 1, j - 1) - u[i0] - v[j];
        if (cur < minv[j]) {
          minv[j] = cur;
          way[j] = j0;
        }
        if (minv[j] < delta) {
          delta = minv[j];
          j1 = j;
        }
      }

      for (let j = 0; j <= m; j++) {
        if (used[j]) {
          u[p[j]] += delta;
          v[j] -= delta;
        } else {
          minv[j] -= delta;
        }
      }
      j0 = j1;
    } while (p[j0] !== 0);

    do {
      const j1 = way[j0];
      p[j0] = p[j1];
      j0 = j1;
    } while (j0 !== 0);
  }

  const result: number[] = new Array(n).fill(-1);
  for (let j = 1; j <= m; j++) {
    const i = p[j];
    if (i > 0 && i <= n) {
      result[i - 1] = j - 1;
    }
  }
  return result;
}

/**
 * Greedy min-cost assignment (simpler, good for small N).
 * Returns assignment: trackIndex -> detectionIndex, or -1 if unassigned.
 */
export function greedyAssignment(
  costMatrix: number[][],
  maxCost: number = Infinity
): number[] {
  const n = costMatrix.length;
  const m = costMatrix[0]?.length ?? 0;
  const assigned = new Array(n).fill(-1);
  const used = new Array(m).fill(false);

  const pairs: { i: number; j: number; c: number }[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < m; j++) {
      pairs.push({ i, j, c: costMatrix[i][j] });
    }
  }
  pairs.sort((a, b) => a.c - b.c);

  for (const { i, j, c } of pairs) {
    if (c > maxCost) break;
    if (assigned[i] >= 0 || used[j]) continue;
    assigned[i] = j;
    used[j] = true;
  }
  return assigned;
}
